import { useEffect } from 'react';
import Lenis from 'lenis';
import { isInteractionLocked, subscribeInteractionLock } from '../utils/interactionLock';

/**
 * Inertia-based smooth scrolling for the whole page.
 * Scrolls the real window, so native scroll listeners (e.g. the drone) keep working.
 * Skipped for visitors who prefer reduced motion.
 */
export function useSmoothScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const lenis = new Lenis({
      // Light glide: short settle, gentle curve, slightly shorter travel per wheel tick
      duration: 0.35,
      easing: (t) => 1 - Math.pow(1 - t, 2),
      wheelMultiplier: 0.95,
      smoothWheel: true,
      // Touch keeps the platform's native momentum
      syncTouch: false,
    });

    // In-page anchor links glide too
    const onClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest('a[href^="#"]');
      if (!link) return;
      const hash = link.getAttribute('href') ?? '#';
      const target = hash === '#' ? 0 : document.querySelector(hash);
      if (target === null) return;
      e.preventDefault();
      lenis.scrollTo(target as HTMLElement | number, { offset: 0 });
    };
    document.addEventListener('click', onClick);

    // Freeze the glide while the site is locked (Pixel's full-charge short)
    const syncLock = () => {
      if (isInteractionLocked()) lenis.stop();
      else lenis.start();
    };
    const unsubscribeLock = subscribeInteractionLock(syncLock);

    // Keep the scrollable length in sync as the page grows (fonts, images, the 3D model loading in).
    // A stale length stopped the scroll short of the page end in some browsers, hiding the footer's bottom.
    const ro = new ResizeObserver(() => lenis.resize());
    ro.observe(document.body);
    const onLoad = () => lenis.resize();
    window.addEventListener('load', onLoad);

    let frame = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      window.removeEventListener('load', onLoad);
      document.removeEventListener('click', onClick);
      unsubscribeLock();
      lenis.destroy();
    };
  }, []);
}
